import api from './api';
import type {
  ApiResponse,
  PaginatedResponse,
  Appointment,
  AppointmentFilters,
  CreateAppointmentData,
  UpdateAppointmentData,
  AppointmentStats,
  AppointmentStatus,
} from '@/types';

/**
 * Rooms / Salla — one entry of the additive `warnings` array the appointment
 * store/update responses carry alongside `data`. Today the backend emits
 * `room_conflict` (over-capacity room); consumers render `message` generically
 * so a new warning `type` needs no frontend change.
 */
export interface AppointmentWarning {
  type: string;
  message: string;
  room_id?: string;
  blockout_id?: string;
}

/**
 * 84-03 (D3): one clashing appointment in a 409 `conflicts` list — the doctor
 * or the patient already has a live visit over the requested window.
 */
export interface AppointmentConflict {
  kind: 'doctor' | 'patient';
  appointment_id: string;
  title: string | null;
  starts_at: string;
  ends_at: string;
  patient: { id: string; full_name: string } | null;
  user: { id: number; full_name: string } | null;
}

/** The 409 body of POST/PUT /appointments when the booking is blocked. */
export interface AppointmentConflictPayload {
  message: string;
  conflicts: AppointmentConflict[];
  /** True when the actor holds appointments.override_conflicts. */
  override_allowed: boolean;
}

/**
 * The conflict payload off a failed create/update, or null for any other
 * error. The axios layer rejects non-401 responses untouched, so the JSON
 * body sits on `error.response.data`.
 */
export function getConflictPayload(error: unknown): AppointmentConflictPayload | null {
  const err = error as { response?: { status?: number; data?: unknown } } | null;
  if (!err || err.response?.status !== 409) return null;
  const data = err.response.data as Partial<AppointmentConflictPayload> | undefined;
  if (!data || !Array.isArray(data.conflicts)) return null;
  return {
    message: typeof data.message === 'string' ? data.message : '',
    conflicts: data.conflicts,
    override_allowed: data.override_allowed === true,
  };
}

/** Partial appointment mutation for a drag-move (same PUT /appointments/:id). */
export interface AppointmentMovePayload {
  scheduled_at?: string;
  duration_minutes?: number;
  /** null clears the room (drop onto the "Pa sallë" column). */
  room_id?: string | null;
  user_id?: number;
  /** ArtDent 261005 — a co-doctor's chip dragged to another doctor's column. */
  co_doctor_ids?: number[];
  /** 84-03: re-send after a 409 with the actor's reason. */
  override_conflicts?: boolean;
  override_reason?: string;
}

/** A create/update result: the resource plus the additive warnings it carried. */
export interface AppointmentMutationResult {
  appointment: Appointment;
  warnings: AppointmentWarning[];
}

/** The store/update body: an AppointmentResource with the optional sibling warnings. */
type AppointmentMutationBody = {
  data: Appointment;
  warnings?: AppointmentWarning[];
};

/**
 * Reads the additive `warnings` array off a store/update response body,
 * tolerating a missing/malformed key. Only entries with a string `message`
 * survive — the message is all the UI renders.
 */
function extractWarnings(body: unknown): AppointmentWarning[] {
  if (!body || typeof body !== 'object') return [];
  const raw = (body as { warnings?: unknown }).warnings;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (w): w is AppointmentWarning =>
      !!w &&
      typeof w === 'object' &&
      typeof (w as { message?: unknown }).message === 'string'
  );
}

// Get appointments with filtering and pagination
export const getAppointments = async (filters: AppointmentFilters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const response = await api.get<PaginatedResponse<Appointment>>(`/appointments?${params}`);
  return response.data;
};

// Get single appointment
export const getAppointment = async (id: string) => {
  const response = await api.get<ApiResponse<Appointment>>(`/appointments/${id}`);
  return response.data.data;
};

// Get ALL of a lead's appointments (unpaginated forLead endpoint — no
// silent truncation, and it eager-loads clinicalVisit).
export const getLeadAppointments = async (leadId: string) => {
  const response = await api.get<{ data: Appointment[] }>(`/leads/${leadId}/appointments`);
  return response.data.data;
};

// Create appointment
// Assignee picker source for the appointment form, keyed by intent:
// calls → active agents (team-scoped server-side for team leads),
// visits → active doctors.
export const getAppointmentAssignableUsers = async (type?: string) => {
  const response = await api.get<{ data: { id: number; full_name: string; email: string }[] }>(
    '/appointments/assignable-users',
    { params: type ? { type } : {} }
  );
  return response.data;
};

/** Why a slot is contended. The staff grid REPORTS these; it never enforces them. */
export type AvailabilityReason =
  | 'busy'
  /** doctor_parallel_visits_enabled: the doctor has a patient here but may take another. */
  | 'doctor_booked'
  | 'blockout'
  | 'over_capacity'
  | 'outside_hours';

export interface AvailabilitySlot {
  /** ISO-8601 start of the slot. */
  start: string;
  /** True only when `reasons` is empty (a lone `doctor_booked` hint aside). */
  available: boolean;
  reasons: AvailabilityReason[];
}

export interface AvailabilityQuery {
  date: string;
  type?: "clinic_visit" | "lead_call";
  user_id?: number;
  doctor_id?: number;
  duration_minutes?: number;
  room_id?: string;
  /** 86-01: leave the appointment being edited out of the busy check. */
  exclude_appointment_id?: string;
}

/**
 * Staff slot grid (mobile booking wizard) — the authenticated sibling of the
 * public /book/availability. Unlike that one it hides nothing: a contended slot
 * still comes back, carrying the reasons, so the UI can grey out a genuine
 * double-book while letting the front desk book over a blockout or a full room.
 */
export const getAppointmentAvailability = async (
  query: AvailabilityQuery
): Promise<AvailabilitySlot[]> => {
  const response = await api.get<{ data: AvailabilitySlot[] }>(
    '/appointments/availability',
    { params: query }
  );
  return response.data.data;
};

// Warnings are ALWAYS preserved (pathfinder U4: the warning-discarding pair
// was deleted; these are the only create/update functions).
export const createAppointment = async (
  data: CreateAppointmentData
): Promise<AppointmentMutationResult> => {
  const response = await api.post<AppointmentMutationBody>('/appointments', data);
  return { appointment: response.data.data, warnings: extractWarnings(response.data) };
};

// Update appointment
export const updateAppointment = async (
  id: string,
  data: UpdateAppointmentData
): Promise<AppointmentMutationResult> => {
  const response = await api.put<AppointmentMutationBody>(`/appointments/${id}`, data);
  return { appointment: response.data.data, warnings: extractWarnings(response.data) };
};

/**
 * PUTs a drag-move via the same `PUT /appointments/:id` endpoint and returns
 * only the additive warnings — all drag-reschedule needs.
 */
export const saveAppointmentMove = async (
  id: string,
  payload: AppointmentMovePayload
): Promise<AppointmentWarning[]> => {
  const response = await api.put<AppointmentMutationBody>(`/appointments/${id}`, payload);
  return extractWarnings(response.data);
};

// Delete appointment
export const deleteAppointment = async (id: string) => {
  await api.delete(`/appointments/${id}`);
};

// Update appointment status
export const updateAppointmentStatus = async (id: string, status: AppointmentStatus) => {
  const response = await api.patch<ApiResponse<Appointment>>(`/appointments/${id}/status`, { status });
  return response.data.data;
};

export const getAppointmentStats = async (type?: string) => {
  const params = type ? `?${new URLSearchParams({ type })}` : '';
  const response = await api.get<AppointmentStats>(`/appointments/stats${params}`);
  return response.data;
};
